/**
 * useEmployeeStats Hook
 * Custom hook for fetching employee statistics
 */

import { useState, useEffect, useCallback } from 'react';
import { EmployeeStats } from '../types/employee';
import employeeService from '../services/employeeService';
import { useSnackbar } from 'notistack';

interface UseEmployeeStatsResult {
  stats: EmployeeStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useEmployeeStats = (autoFetch = true): UseEmployeeStatsResult => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const rawData = await employeeService.getEmployeeStats();
      console.log('📊 [useEmployeeStats] Raw stats data:', rawData);
      
      // Transformar dados da API para o formato esperado
      // API retorna: { ATIVO: 13, DEMITIDO: 1, ... }
      // Frontend espera: { total: X, byStatus: { ATIVO: 13, ... }, pcdCount: 0, ... }
      const transformedStats: EmployeeStats = {
        total: Object.values(rawData).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : 0), 0),
        byStatus: rawData as any,
        byGender: {} as any,
        byDepartment: {},
        byOccupation: {},
        pcdCount: 0,
        cipaCount: 0,
        minorsCount: 0
      };
      
      console.log('✨ [useEmployeeStats] Transformed stats:', transformedStats);
      setStats(transformedStats);
    } catch (err: any) {
      console.error('❌ [useEmployeeStats] Error:', err);
      const errorMessage = err.response?.data?.message || 'Erro ao carregar estatísticas';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (autoFetch) {
      fetchStats();
    }
  }, [autoFetch, fetchStats]);

  const refresh = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh
  };
};
